import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useApp } from '@/context/AppContext';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom vessel icon creator
const createVesselIcon = (heading, status) => {
  const colorMap = {
    'Underway': '#22C55E',
    'At Anchor': '#1E88C7',
    'Moored': '#0891B2',
    'default': '#64748B'
  };
  
  const color = colorMap[status] || colorMap.default;
  
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" style="transform: rotate(${heading}deg);">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.3"/>
        </filter>
      </defs>
      <path d="M12 2 L20 20 L12 16 L4 20 Z" fill="${color}" stroke="#ffffff" stroke-width="1.5" filter="url(#shadow)"/>
    </svg>
  `;
  
  return L.divIcon({
    html: svgIcon,
    className: 'vessel-marker',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};

// Map position controller component
const MapController = () => {
  const map = useMap();
  const { mapCenter, mapZoom } = useApp();
  
  useEffect(() => {
    if (mapCenter) {
      map.setView(mapCenter, mapZoom, { animate: true });
    }
  }, [map, mapCenter, mapZoom]);
  
  return null;
};

// Vessel marker component
const VesselMarker = ({ vessel, onClick }) => {
  const icon = useMemo(() => 
    createVesselIcon(vessel.heading, vessel.status), 
    [vessel.heading, vessel.status]
  );

  return (
    <Marker
      position={[vessel.lat, vessel.lon]}
      icon={icon}
      eventHandlers={{
        click: () => onClick(vessel)
      }}
    >
      <Popup className="vessel-popup">
        <div className="p-2 min-w-[200px]">
          <h3 className="font-semibold text-foreground mb-2">{vessel.name}</h3>
          <div className="space-y-1 text-sm">
            <p><span className="text-muted-foreground">MMSI:</span> {vessel.mmsi}</p>
            <p><span className="text-muted-foreground">SOG:</span> {vessel.sog} kn</p>
            <p><span className="text-muted-foreground">COG:</span> {vessel.cog}°</p>
            <p><span className="text-muted-foreground">Status:</span> {vessel.status}</p>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

// Geofence polygon component
const GeofencePolygon = ({ geofence, onClick }) => {
  return (
    <Polygon
      positions={geofence.polygonCoords}
      pathOptions={{
        color: geofence.color || '#1E88C7',
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0.15,
        dashArray: '5, 5'
      }}
      eventHandlers={{
        click: () => onClick && onClick(geofence)
      }}
    >
      <Popup>
        <div className="p-2">
          <h3 className="font-semibold">{geofence.name}</h3>
          <p className="text-sm text-muted-foreground">{geofence.portAreaType}</p>
        </div>
      </Popup>
    </Polygon>
  );
};

// Station boundary component
const StationBoundary = ({ station, isSelected }) => {
  return (
    <Polygon
      positions={station.boundaryCoords}
      pathOptions={{
        color: isSelected ? '#FB8C00' : '#64748B',
        weight: isSelected ? 3 : 2,
        opacity: isSelected ? 1 : 0.6,
        fillOpacity: isSelected ? 0.2 : 0.1,
        dashArray: isSelected ? null : '3, 6'
      }}
    >
      <Popup>
        <div className="p-2">
          <h3 className="font-semibold">{station.name}</h3>
          <p className="text-sm text-muted-foreground">Max Speed: {station.maxSpeed} kn</p>
        </div>
      </Popup>
    </Polygon>
  );
};

export const VesselMap = ({ 
  showGeofences = true, 
  showStations = false,
  onGeofenceClick,
  highlightedGeofence = null
}) => {
  const mapRef = useRef(null);
  const { 
    vessels, 
    geofences, 
    stations,
    selectedStation,
    mapStyle,
    selectVessel 
  } = useApp();

  const tileUrl = mapStyle === 'satellite' 
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

  const tileAttribution = mapStyle === 'satellite'
    ? 'Tiles &copy; Esri'
    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  return (
    <MapContainer
      ref={mapRef}
      center={[-5.12, 119.42]}
      zoom={13}
      className="w-full h-full rounded-lg"
      zoomControl={true}
    >
      <TileLayer
        attribution={tileAttribution}
        url={tileUrl}
      />
      
      <MapController />

      {/* Render geofences */}
      {showGeofences && geofences.map((geofence) => (
        <GeofencePolygon 
          key={geofence.id} 
          geofence={geofence}
          onClick={onGeofenceClick}
        />
      ))}

      {/* Render highlighted geofence */}
      {highlightedGeofence && (
        <Polygon
          positions={highlightedGeofence.polygonCoords}
          pathOptions={{
            color: highlightedGeofence.color || '#1E88C7',
            weight: 4,
            opacity: 1,
            fillOpacity: 0.3
          }}
        />
      )}

      {/* Render stations */}
      {showStations && stations.map((station) => (
        <StationBoundary 
          key={station.id} 
          station={station}
          isSelected={selectedStation?.id === station.id}
        />
      ))}

      {/* Render vessel markers */}
      {vessels.map((vessel) => (
        <VesselMarker
          key={vessel.id}
          vessel={vessel}
          onClick={selectVessel}
        />
      ))}
    </MapContainer>
  );
};

export default VesselMap;
